
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BanknoteIcon, Loader2, Info } from 'lucide-react'; // Info importiert
import { useToast } from '@/components/ui/use-toast';
import { ScrollArea } from '@/components/ui/scroll-area';
import { initializePaymentGateway, markListingAsPremium } from '@/lib/paypal';
import { recordPremiumPayment } from '@/lib/actions/premiumActions';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { isCapacitorApp } from '@/lib/utils'; // Neu importiert
import { Alert, AlertDescription, AlertTitle as UiAlertTitle } from '@/components/ui/alert'; // UiAlertTitle als Alias

interface PremiumOptionsProps {
  onSelect: (method: 'paypal') => void;
  listingId?: number;
  onClose: () => void;
}

const PremiumOptions = ({ onSelect, onClose, listingId }: PremiumOptionsProps) => {
  const { toast } = useToast();
  const { profile } = useAuth();
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const paypalButtonRef = useRef<HTMLDivElement>(null);
  const runningInCapacitor = isCapacitorApp();

  // Initialize PayPal SDK
  useEffect(() => {
    if (runningInCapacitor) {
      // Don't load PayPal SDK if in Capacitor app for this component's direct rendering
      // The payment will be handled differently or users guided to web
      setPaypalLoaded(false); // Explicitly set to false or manage state accordingly
      return;
    }
    const loadPayPal = async () => {
      try {
        await initializePaymentGateway();
        setPaypalLoaded(true);
      } catch (error) {
        console.error('Failed to load PayPal:', error);
        toast({
          title: "PayPal nuk mund të ngarkohej",
          description: "Pati një problem në ngarkimin e PayPal. Ju lutemi provoni më vonë.",
        });
      }
    };

    loadPayPal();
  }, [toast, runningInCapacitor]);

  // Render PayPal buttons when SDK is loaded (and not in Capacitor)
  useEffect(() => {
    if (paypalLoaded && !runningInCapacitor && paypalButtonRef.current && window.paypal && listingId) {
      // Clear any existing buttons
      paypalButtonRef.current.innerHTML = '';
      
      window.paypal.Buttons({
        createOrder: (data: any, actions: any) => {
          return actions.order.create({
            intent: "CAPTURE", // Add this line to fix the error
            purchase_units: [{
              amount: {
                value: '5.00',
                currency_code: 'EUR'
              },
              description: 'Shërbimi premium për 28 ditë'
            }]
          });
        },
        onApprove: async (data: any, actions: any) => {
          setIsProcessing(true);
          
          try {
            // Capture the payment
            const details = await actions.order.capture();
            console.log('PayPal payment completed:', details);
            
            // Record the premium payment
            if (listingId && profile?.id) {
              // First record the payment in our database
              await recordPremiumPayment(listingId, profile.id);
              
              // Then mark the listing as premium
              const success = await markListingAsPremium(listingId);
              
              if (success) {
                onSelect('paypal');
                toast({
                  title: "Pagesa u realizua me sukses",
                  description: "Shpallja juaj tani është Premium",
                });
              } else {
                throw new Error('Gabim gjatë aktivizimit të statusit premium');
              }
            }
          } catch (error) {
            console.error('PayPal payment error:', error);
            toast({
              title: "Gabim pagese",
              description: "Pati një problem gjatë përpunimit të pagesës tuaj. Ju lutemi provoni përsëri.",
              variant: "destructive"
            });
          } finally {
            setIsProcessing(false);
          }
        },
        onError: (err: any) => {
          console.error('PayPal error:', err);
          toast({
            title: "Gabim PayPal",
            description: "Pati një problem me PayPal. Ju lutemi provoni përsëri.",
            variant: "destructive"
          });
        }
      }).render(paypalButtonRef.current);
    }
  }, [paypalLoaded, runningInCapacitor, paypalButtonRef, onSelect, listingId, toast, profile]);

  return (
    <ScrollArea className="h-full max-h-[80vh]">
      <div className="space-y-6 pr-4">
        <div>
          <h2 className="text-2xl font-bold text-center mb-2">Përmirëso në Premium</h2>
          <p className="text-center text-gray-500 mb-6">
            Bëni shpalljen tuaj të dallohet me dukshmëri premium për vetëm 5€ për 28 ditë
          </p>
        </div>
        
        {/* PayPal Option */}
        <Card className="cursor-pointer hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center">
              <BanknoteIcon className="mr-2 h-5 w-5" /> 
              PayPal
            </CardTitle>
            <CardDescription>Paguaj me PayPal ose kartë</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-500 mb-3">Pagesë e shpejtë dhe e sigurt me PayPal</p>
            {runningInCapacitor ? (
              <Alert>
                <Info className="h-4 w-4" />
                <UiAlertTitle>PayPal Zahlung</UiAlertTitle>
                <AlertDescription>
                  Um Ihren Premium-Kauf über PayPal abzuschließen, besuchen Sie bitte unsere Webseite in einem Standard-Webbrowser. 
                  PayPal-Zahlungen innerhalb der App werden für ein optimales Erlebnis derzeit am besten über die Webseite abgewickelt.
                </AlertDescription>
              </Alert>
            ) : (
              <div ref={paypalButtonRef} className="w-full min-h-10">
                {!paypalLoaded && !runningInCapacitor && (
                  <div className="flex justify-center items-center h-10">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        
        <div className="pt-4 text-center">
          <Button variant="ghost" onClick={onClose}>
            Anulo
          </Button>
        </div>
      </div>
    </ScrollArea>
  );
};

export default PremiumOptions;

